/**
 * Client-side status overrides for tasks the server reports as not started
 * although, for this user, there is nothing left to do.
 *
 * Campaign Manager hands every user the same task list. A KYC task completes
 * on the `kyc.level_completed` event, so for a user verified before the task
 * existed the server can still report NOT_STARTED. Such a user should not be
 * sent to the KYC flow again — and the back end does NOT reward a KYC done
 * before the task, so the task is simply not available to them: the card is
 * the design's inactive tile (faded, a disabled «انجام شده» button, the
 * server's own status on the chip) and the list moves it to the end.
 *
 * Display-only. Completion and rewards are decided by the back end; once it
 * reports the task COMPLETED, its own state is shown and this rule is skipped.
 *
 * Kept pure (no hooks) so the list, the card and the details modal all reach
 * the same conclusion, like `cta.ts`.
 */
import { Task, TaskDestinationValues, UserTaskStatusValues } from './types';

export interface TaskUnavailability {
  /**
   * Status chip label; when `inactive`, the disabled button's label instead
   * and the chip keeps the server's status (Figma node 68819:169726).
   */
  label: string;
  /** One line for the details modal explaining the state. */
  reason: string;
  /**
   * true → the design's inactive tile: faded, with a disabled button.
   * false → a normal-looking card that simply has nothing to do (no button).
   */
  inactive: boolean;
  /** Tint of the toned status badge in the details modal. */
  tone: 'positive' | 'negative';
}

/**
 * Campaign Manager's KYC levels: 2 = basic (پایه/متوسط), 3 = advanced. The
 * operator may override the level a task targets with `metadata.kyc_level`;
 * without it the task is assumed to be the basic (level 2) one.
 */
const DEFAULT_KYC_TASK_LEVEL = 2;

const kycTaskLevel = (task: Task): number => {
  const raw = task.metadata?.kyc_level;
  const level = typeof raw === 'string' ? Number(raw) : raw;
  return typeof level === 'number' && Number.isInteger(level) && level > 0
    ? level
    : DEFAULT_KYC_TASK_LEVEL;
};

const isKycTask = (task: Task): boolean =>
  task.action?.destination === TaskDestinationValues.KYC;

export interface EligibilityContext {
  /**
   * The user's approved KYC level on Campaign Manager's scale (1 = none,
   * 2 = basic, 3 = advanced), or null while it is still loading or unknown.
   * Unknown never overrides a task.
   */
  kycLevel: number | null;
}

/**
 * Returns the override for this user's view of the task, or null to show the
 * server's state as is.
 *
 * A task the server already reports as finished is left alone: its own
 * completed/claimed/waiting state says more than any override.
 */
export const resolveTaskUnavailability = (
  task: Task,
  { kycLevel }: EligibilityContext
): TaskUnavailability | null => {
  const unfinished =
    task.userStatus === UserTaskStatusValues.NOT_STARTED ||
    task.userStatus === UserTaskStatusValues.IN_PROGRESS;
  if (!unfinished) return null;

  if (isKycTask(task) && kycLevel !== null && kycLevel >= kycTaskLevel(task)) {
    return {
      label: 'انجام شده',
      reason:
        'احراز هویت شما پیش‌تر انجام شده است؛ این چالش برای شما جایزه‌ای ندارد.',
      inactive: true,
      tone: 'positive'
    };
  }

  return null;
};
